import React, { useCallback, useState } from 'react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { postInternalReview } from '@/modules/Reviews/ReviewsServiceHelper'
import { useNotificationError } from '@/modules/Notifications'

import VariantImages from '@/assets/VariantImages'
import Box from '@mui/material/Box'
import {
  EmtAvatar,
  EmtButton,
  EmtIcon,
  EmtModal,
  EmtRating,
  EmtSeparator,
  EmtTextarea,
  EmtTypography,
  useMobile,
} from 'emt-components-v2'
import LinkButton from '@/components/EMT/LinkButton'
import { PostReview } from '@/modules/Reviews/apiTypes'
import useDeviceDetector from '@/hooks/useDeviceDetector'
import { MessageEventData_ShowNotation } from '@/data'
import { MemberProfile } from '@/dataV3/User/MemberProfile'
import AskReviewModal from './AskReviewModal'
import { useIsQueryOwner } from '@/hooks'

import { answerReview } from '@/modules/Reviews/ReviewsServiceHelper'
import { Routes } from '@/assets/configs'

import { usePetsittingMode } from '@/store/usePetsittingMode'

type Props = React.PropsWithChildren<{
  profile: Pick<MemberProfile, 'displayName' | 'reviewsSummary' | 'id'> & { '@review'?: MemberProfile['@review'] }
  activeForm?: boolean
  showGiveFeedback?: boolean
  buttonColor?: string
  variant?: 'my-profile' | 'default'
  pro?: boolean
  onReplySuccess?: () => void
  hasMoreReviews?: boolean
  reviewsLoadingMore?: boolean
  onLoadMoreReviews?: () => void
}>

const Comments: React.FC<Props> = ({
  profile: { displayName, reviewsSummary, id: profileId },
  activeForm = true,
  buttonColor = 'primary',
  variant = 'default',
  pro = false,
  showGiveFeedback = false,
  onReplySuccess,
  hasMoreReviews = false,
  reviewsLoadingMore = false,
  onLoadMoreReviews,
}) => {
  const { t } = useTranslation('profile')
  const { t: tCommon } = useTranslation('common')
  const { isPetsittingMode } = usePetsittingMode()
  const [isMobile] = useMobile()
  const [activeModalComment, setActiveModalComment] = useState<boolean>(false)
  const [askModalComment, setAskModalComment] = useState<boolean>(false)
  const [addNotificationError] = useNotificationError()
  const { device } = useDeviceDetector()
  const [isQueryOwner] = useIsQueryOwner()
  const router = useRouter()
  const careId = router.query['careId'] as string

  const generateBaseComment = () => ({
    rating: 0,
    content: '',
  })

  const [comment, setComment] = useState<PostReview.Body>(generateBaseComment())
  const [commentIsLoading, setCommentIsLoading] = useState<boolean>(false)
  const [commentIsPosted, setCommentIsPosted] = useState<boolean>(false)
  const [replyByReviewId, setReplyByReviewId] = useState<Record<string, string>>({})
  const [replyLoadingByReviewId, setReplyLoadingByReviewId] = useState<Record<string, boolean>>({})
  const [replyPostedByReviewId, setReplyPostedByReviewId] = useState<Record<string, boolean>>({})
  const displayedReviewsCount = reviewsSummary.reviews?.length || 0
  const totalReviewsCount = reviewsSummary.reviewsCount || 0
  const canLoadMoreReviews =
    !!onLoadMoreReviews && !!hasMoreReviews && totalReviewsCount > 0 && displayedReviewsCount < totalReviewsCount

  const setReplyValue = (reviewId: string, value: string) => {
    setReplyByReviewId((prev) => ({ ...prev, [reviewId]: value }))
  }

  const postReply = useCallback(async (reviewId: string, content: string) => {
    return answerReview(reviewId, { content })
  }, [])

  const handleSaveReply = async (reviewId: string) => {
    const content = (replyByReviewId[reviewId] ?? '').trim()

    setReplyLoadingByReviewId((p) => ({ ...p, [reviewId]: true }))
    try {
      await postReply(reviewId, content)
      setReplyPostedByReviewId((p) => ({ ...p, [reviewId]: true }))
      if (onReplySuccess) onReplySuccess()
    } catch (error) {
      addNotificationError({ error })
    } finally {
      setReplyLoadingByReviewId((p) => ({ ...p, [reviewId]: false }))
    }
  }

  const handlePostComment = () => {
    setCommentIsLoading(true)
    postComment(comment)
      .then(() => {
        setActiveModalComment(true)
        setCommentIsPosted(true)
      })
      .catch((error) => {
        addNotificationError({ error })
      })
      .finally(() => {
        setCommentIsLoading(false)
      })
  }

  /**
   * ? [🔥 API Calls]
   */
  const postComment = useCallback(
    async (review: PostReview.Body) => {
      if (profileId && activeForm) {
        return postInternalReview({
          to: profileId,
          content: review.content,
          rating: review.rating,
          careId: careId ?? undefined,
        })
      }
    },
    [profileId, activeForm],
  )

  // Show the notation in the RN webview
  function showNotation() {
    // Make sure we have a user and the RN webview is available
    if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined') {
      const eventData: MessageEventData_ShowNotation = {
        event: 'showNotation',
      }
      // Post the message only once
      window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
    } else {
      // If webview is not available, open the appropriate store
      if (device === 'iOS') {
        isPetsittingMode
          ? window.open('https://apps.apple.com/fr/app/super-sitters/id6759970836 ', '_blank')
          : window.open('https://apps.apple.com/app/emprunte-mon-toutou/id1403471929?action=write-review', '_blank')
      } else if (device === 'Android') {
        isPetsittingMode
          ? window.open('https://play.google.com/store/apps/details?id=app.supersitters.app', '_blank')
          : window.open('https://play.google.com/store/apps/details?id=com.empruntemontoutou.app', '_blank')
      }
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: variant === 'my-profile' ? 'auto' : '100%',
              gap: '8px',
            }}
          >
            <EmtTypography variant="h4" fontWeight={800}>
              {isQueryOwner
                ? t('comments.reviewName', {
                    count: reviewsSummary.reviews ? reviewsSummary.reviewsCount : 0,
                    name: displayName,
                  })
                : t('comments.review', {
                    count: reviewsSummary.reviews ? reviewsSummary.reviewsCount : 0,
                  })}
            </EmtTypography>
            <EmtRating
              defaultValue={reviewsSummary.averageRating}
              label={reviewsSummary.averageRating}
              labelFontSize="h5"
              labelFontColor="greyGradient.light"
              max={1}
              readOnly
              size="large"
              sx={{ marginRight: '3px' }}
            />
          </Box>
          {variant === 'my-profile' && (
            <EmtButton
              color={pro ? 'purple' : 'primary'}
              size="small"
              variant="outlined"
              onClick={() => setAskModalComment(true)}
            >
              {t('comments.askReviews')}
            </EmtButton>
          )}
        </Box>
        <EmtSeparator />
        {/* Comment ITEMS */}
        {reviewsSummary.reviews && reviewsSummary.reviews.length
          ? reviewsSummary.reviews.map((review, key) => (
              <React.Fragment key={review.id}>
                <Box key={review.id} sx={{ display: 'flex', flexDirection: 'column', rowGap: '12px' }}>
                  <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <EmtAvatar
                        src={
                          review.thumbnailUrl
                            ? review.thumbnailUrl
                            : isPetsittingMode
                            ? VariantImages.SSSelect?.default?.src || VariantImages.SSSelect?.src
                            : VariantImages.EMT?.default?.src || VariantImages.EMT?.src
                        }
                        alt={review.displayName}
                      />
                      <EmtTypography variant="h5" fontWeight={700}>
                        {review.displayName} <br />
                        {review.reviewDate && (
                          <EmtTypography variant="body2" color="greyGradient.light">
                            {DateTime.fromISO(review.reviewDate).toLocaleString(DateTime.DATE_MED)}
                          </EmtTypography>
                        )}
                      </EmtTypography>
                    </Box>
                    <EmtRating defaultValue={review.rating} labelFontSize="h5" max={5} readOnly size="medium" />
                  </Box>
                  <EmtTypography variant="body1">{review.comment}</EmtTypography>
                  {/* Reply UI (only on my profile) */}
                  {showGiveFeedback && review?.id && !review.answer && review.author !== 'Emprunte Mon Toutou' && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '12px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          rowGap: '12px',
                          borderLeft: '4px solid #EAEAEC',
                          paddingLeft: '12px',
                        }}
                      >
                        <EmtTypography variant="h5" fontWeight={700}>
                          {t('comments.answerTo', { name: review.displayName })}
                        </EmtTypography>

                        <EmtTextarea
                          label={t('comments.writeResponse') as string}
                          hideLabelOnClick
                          maxLength={200}
                          value={replyByReviewId[review.id] ?? ''}
                          onChange={(value) => setReplyValue(review.id, value)}
                          sx={{ minHeight: 90 }}
                          preventScroll
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                        <EmtButton
                          size="small"
                          color={pro ? 'purple' : 'primary'}
                          onClick={() => handleSaveReply(review.id)}
                          loader={!!replyLoadingByReviewId[review.id]}
                          disabled={
                            (replyByReviewId[review.id] ?? '').trim().length < 5 || !!replyPostedByReviewId[review.id]
                          }
                        >
                          {tCommon('save')}
                        </EmtButton>

                        {isQueryOwner && (
                          <LinkButton
                            variant="outlined"
                            size="small"
                            color={pro ? 'purple' : 'primary'}
                            href={`${Routes.Profile(review.authorId)}#reviews`}
                            disabled={!!replyLoadingByReviewId[review.id]}
                          >
                            {t('comments.giveFeedbackTo', { name: review.displayName })}
                          </LinkButton>
                        )}
                      </Box>
                    </Box>
                  )}
                  {review.answer && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '8px', alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          padding: '12px',
                          backgroundColor: 'greyGradient.extraLight',
                          borderLeft: '4px solid #EAEAEC',
                          paddingLeft: '12px',
                        }}
                      >
                        <EmtTypography variant="body2" fontWeight={700} sx={{ marginBottom: '8px' }}>
                          {activeForm ? t('comments.responseOf', { name: displayName }) : t('comments.yourResponse')}
                        </EmtTypography>
                        <EmtTypography variant="body1">{review.answer}</EmtTypography>
                      </Box>
                      {showGiveFeedback && isQueryOwner && (
                        <LinkButton
                          variant="outlined"
                          color={pro ? 'purple' : 'primary'}
                          size="small"
                          href={`${Routes.Profile(review.authorId)}#reviews`}
                          disabled={!!replyLoadingByReviewId[review.id]}
                        >
                          {t('comments.giveFeedbackTo', { name: review.displayName })}
                        </LinkButton>
                      )}
                    </Box>
                  )}
                </Box>

                {reviewsSummary.reviews.length - 1 !== Number(key) && <EmtSeparator />}
              </React.Fragment>
            ))
          : null}
        {/* END Comment ITEMS */}

        {canLoadMoreReviews && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <EmtButton
              variant="outlined"
              color={pro ? 'purple' : 'primary'}
              size="small"
              onClick={onLoadMoreReviews}
              loader={reviewsLoadingMore}
              disabled={reviewsLoadingMore}
            >
              {t('comments.loadMore', { defaultValue: 'Load more' })}
            </EmtButton>
          </Box>
        )}

        {activeForm && reviewsSummary.reviews && reviewsSummary.reviews.length ? <EmtSeparator /> : null}

        {/* Add comment */}
        {activeForm && !commentIsPosted && (
          <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px', marginTop: '16px' }}>
            <EmtTypography variant="h4" fontWeight={800}>
              {isQueryOwner
                ? t('comments.titleName', { name: displayName })
                : t('comments.title', { name: displayName })}
            </EmtTypography>
            <EmtRating
              filled={false}
              max={5}
              size="large"
              emptyIconColor="greyGradient.light"
              sx={{
                marginTop: '-5px',
                '& .emt-icons': {
                  fontSize: '24px',
                },
              }}
              value={comment.rating}
              onChange={(ratingEvent, ratingValue) => {
                setComment((prev) => ({ ...prev, rating: ratingValue || 0 }))
              }}
            />
            <EmtTextarea
              label={
                t('comments.textareaPlaceholder', {
                  name: displayName,
                }) as string
              }
              hideLabelOnClick
              maxLength={500}
              onChange={(value) => setComment((prev) => ({ ...prev, content: value }))}
              sx={{ minHeight: 150 }}
              value={comment.content}
              preventScroll
            />
            <Box>
              <EmtButton
                onClick={handlePostComment}
                disabled={!comment.rating || !comment.content || comment.content.length < 5}
                loader={commentIsLoading}
                className="reviewga"
                color={buttonColor || (pro ? 'purple' : 'primary')}
              >
                {t('comments.send')}
              </EmtButton>
            </Box>
          </Box>
        )}

        {/* END Add comment */}
      </Box>

      {/* Ask review Modal */}
      <AskReviewModal open={askModalComment} onClose={() => setAskModalComment(false)} />
      {/* END Ask review Modal */}

      {/* MODAL Comment */}
      <EmtModal
        variant="default"
        open={activeModalComment}
        onClose={() => {
          setComment(generateBaseComment)
          setActiveModalComment(false)
        }}
        sx={{ zIndex: 9999 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '15px',
          }}
        >
          <EmtTypography variant="h4" textAlign="center" fontWeight={800}>
            {t('comments.modal.title')}
          </EmtTypography>

          <EmtTypography variant="body1" textAlign="center">
            <Trans t={t} i18nKey={`comments.modal.content.base`} />
          </EmtTypography>
          {comment.rating > 4 && (
            <EmtTypography variant="body1" textAlign="center">
              <b>
                <Trans t={t} i18nKey={`comments.modal.content.${device}`} />
              </b>
            </EmtTypography>
          )}

          <Box sx={{ display: 'flex', gap: '15px', flexDirection: isMobile ? 'column' : 'row', rowGap: '16px' }}>
            {comment.rating <= 4 && (
              <EmtButton
                size="large"
                color={pro ? 'purple' : 'primary'}
                variant="whiteOutlined"
                onClick={() => {
                  setComment(generateBaseComment)
                  setActiveModalComment(false)
                }}
                fullWidth={isMobile}
              >
                {t('comments.modal.buttons.back')}
              </EmtButton>
            )}

            {comment.rating > 4 &&
              (device === 'iOS' || device === 'Android' ? (
                <EmtButton color={pro ? 'purple' : 'primary'} size="large" onClick={showNotation} fullWidth={isMobile}>
                  <span style={{ marginRight: '5px', lineHeight: '1' }}>{t(`comments.modal.buttons.base`)}</span>
                  {Array(5)
                    .fill(null)
                    .map((_, index) => (
                      <EmtIcon key={index} className="emt-star" sx={{ color: '#FFC10A' }} />
                    ))}
                </EmtButton>
              ) : (
                <LinkButton
                  href={t(`comments.modal.links.Other`)}
                  color={pro ? 'purple' : 'primary'}
                  size="large"
                  onClick={() => {
                    setComment(generateBaseComment)
                    setActiveModalComment(false)
                  }}
                  fullWidth={isMobile}
                >
                  <span style={{ marginRight: '5px', lineHeight: '1' }}>{t(`comments.modal.buttons.base`)}</span>
                  {Array(5)
                    .fill(null)
                    .map((_, index) => (
                      <EmtIcon key={index} className="emt-star" sx={{ color: '#FFC10A' }} />
                    ))}
                </LinkButton>
              ))}
          </Box>
        </Box>
      </EmtModal>
      {/* END MODAL Comment */}
    </>
  )
}

export default Comments
